import { createEffect, onCleanup, onMount } from "solid-js"
import { createStore } from "solid-js/store"
import { createSimpleContext } from "../context/helper"
import { DEFAULT_THEMES } from "./default-themes"
import { resolveThemeVariant, themeToCss } from "./resolve"
import type { DesktopTheme } from "./types"

export type ColorScheme = "light" | "dark" | "system"

export type ThemePersistence = {
  load(): { themeId?: string; colorScheme?: ColorScheme }
  saveTheme(themeId: string): void
  saveColorScheme(colorScheme: ColorScheme): void
  cacheCss(mode: "light" | "dark", css: string): void
  clearCss(): void
  subscribe(listener: (change: { themeId?: string; colorScheme?: ColorScheme }) => void): () => void
}

const THEME_STYLE_ID = "mux-theme"

function ensureThemeStyleElement(): HTMLStyleElement {
  const existing = document.getElementById(THEME_STYLE_ID) as HTMLStyleElement | null
  if (existing) return existing
  const element = document.createElement("style")
  element.id = THEME_STYLE_ID
  document.head.appendChild(element)
  return element
}

function getSystemMode(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
}

function chromeColor(tokens: Record<string, string>, isDark: boolean) {
  const bg = tokens["background-base"]
  if (typeof bg === "string" && bg.startsWith("#")) return bg
  return isDark ? "#080808" : "#fafafa"
}

function applyThemeCss(theme: DesktopTheme, themeId: string, mode: "light" | "dark", persistence: ThemePersistence) {
  const isDark = mode === "dark"
  const variant = isDark ? theme.dark : theme.light
  const tokens = resolveThemeVariant(variant, isDark)
  const css = themeToCss(tokens)
  const chrome = chromeColor(tokens, isDark)

  if (themeId !== "mux") {
    persistence.cacheCss(mode, css)
  }

  const fullCss = `:root {
  color-scheme: ${mode};
  --text-mix-blend-mode: ${isDark ? "plus-lighter" : "multiply"};
  ${css}
}`

  document.getElementById("mux-theme-preload")?.remove()
  ensureThemeStyleElement().textContent = fullCss
  document.documentElement.dataset.theme = themeId
  document.documentElement.dataset.colorScheme = mode
  document.documentElement.style.backgroundColor = chrome
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute("content", chrome)
}

function cacheThemeVariants(theme: DesktopTheme, themeId: string, persistence: ThemePersistence) {
  if (themeId === "mux") return
  for (const mode of ["light", "dark"] as const) {
    const isDark = mode === "dark"
    const variant = isDark ? theme.dark : theme.light
    const tokens = resolveThemeVariant(variant, isDark)
    const css = themeToCss(tokens)
    persistence.cacheCss(mode, css)
  }
}

export const { use: useTheme, provider: ThemeProvider } = createSimpleContext({
  name: "Theme",
  init: (props: {
    defaultTheme?: string
    persistence: ThemePersistence
    onThemeApplied?: (theme: DesktopTheme, mode: "light" | "dark") => void
  }) => {
    const saved = props.persistence.load()
    const initialThemeId = saved.themeId ?? props.defaultTheme ?? "mux"
    const initialScheme = saved.colorScheme ?? "system"
    const [store, setStore] = createStore({
      themes: DEFAULT_THEMES as Record<string, DesktopTheme>,
      themeId: initialThemeId,
      colorScheme: initialScheme as ColorScheme,
      mode: initialScheme === "system" ? getSystemMode() : initialScheme,
      previewThemeId: null as string | null,
      previewScheme: null as ColorScheme | null,
    })

    onCleanup(
      props.persistence.subscribe((change) => {
        if (change.themeId) setStore("themeId", change.themeId)
        if (change.colorScheme) {
          setStore("colorScheme", change.colorScheme)
          setStore("mode", change.colorScheme === "system" ? getSystemMode() : change.colorScheme)
        }
      }),
    )

    onMount(() => {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
      const handler = () => {
        if (store.colorScheme === "system") {
          setStore("mode", getSystemMode())
        }
      }
      mediaQuery.addEventListener("change", handler)
      onCleanup(() => mediaQuery.removeEventListener("change", handler))

      const currentTheme = store.themes[store.themeId]
      if (currentTheme) {
        cacheThemeVariants(currentTheme, store.themeId, props.persistence)
      }
    })

    const applyTheme = (theme: DesktopTheme, themeId: string, mode: "light" | "dark") => {
      applyThemeCss(theme, themeId, mode, props.persistence)
      props.onThemeApplied?.(theme, mode)
    }

    createEffect(() => {
      const theme = store.themes[store.themeId]
      if (theme) {
        applyTheme(theme, store.themeId, store.mode)
      }
    })

    const setTheme = (id: string) => {
      const theme = store.themes[id]
      if (!theme) {
        console.warn(`Theme "${id}" not found`)
        return
      }
      setStore("themeId", id)
      props.persistence.saveTheme(id)
      if (id === "mux") {
        props.persistence.clearCss()
        return
      }
      cacheThemeVariants(theme, id, props.persistence)
    }

    const setColorScheme = (scheme: ColorScheme) => {
      setStore("colorScheme", scheme)
      props.persistence.saveColorScheme(scheme)
      setStore("mode", scheme === "system" ? getSystemMode() : scheme)
    }

    return {
      themeId: () => store.themeId,
      colorScheme: () => store.colorScheme,
      mode: () => store.mode,
      themes: () => store.themes,
      setTheme,
      setColorScheme,
      registerTheme: (theme: DesktopTheme) => setStore("themes", theme.id, theme),
      previewTheme: (id: string) => {
        const theme = store.themes[id]
        if (!theme) return
        setStore("previewThemeId", id)
        const previewMode = store.previewScheme
          ? store.previewScheme === "system"
            ? getSystemMode()
            : store.previewScheme
          : store.mode
        applyTheme(theme, id, previewMode)
      },
      previewColorScheme: (scheme: ColorScheme) => {
        setStore("previewScheme", scheme)
        const previewMode = scheme === "system" ? getSystemMode() : scheme
        const id = store.previewThemeId ?? store.themeId
        const theme = store.themes[id]
        if (theme) {
          applyTheme(theme, id, previewMode)
        }
      },
      commitPreview: () => {
        if (store.previewThemeId) {
          setTheme(store.previewThemeId)
        }
        if (store.previewScheme) {
          setColorScheme(store.previewScheme)
        }
        setStore("previewThemeId", null)
        setStore("previewScheme", null)
      },
      cancelPreview: () => {
        setStore("previewThemeId", null)
        setStore("previewScheme", null)
        const theme = store.themes[store.themeId]
        if (theme) {
          applyTheme(theme, store.themeId, store.mode)
        }
      },
    }
  },
})
